import { Controller } from "@hotwired/stimulus"

// ============================================================
// SlotDeleteConfirmController — 계정(슬롯) 삭제 확인 모달
// ============================================================
// /accounts 카드의 "삭제" 버튼이 이 컨트롤러의 open(event) 을 호출해 단순 예/아니오
// 확인 모달을 띄운다. "예" 클릭 시 DELETE 요청을 실제로 제출하고, "아니오"/ESC/오버레이
// 클릭 시 닫는다. 실삭제는 서버(AccountsController#destroy)가 D서버에 위임 처리한다.
//
// WHY 동적 폼 제출:
//   카드마다 button_to 숨김 폼을 두는 대신, 페이지에 모달 1개만 두고 클릭한 버튼의
//   data-delete-url 로 DELETE 폼을 즉석 생성한다(다중 카드 → 단일 모달 공유).
//   requestSubmit() 이어야 submit 이벤트가 발생해 Turbo 가 응답(302 리다이렉트 또는
//   409 turbo_stream)을 가로챈다. form.submit() 은 Turbo 를 우회하므로 쓰지 않는다.
//
// Targets:
//   modal    — 표시/숨길 모달 컨테이너
//   altLabel — "ALT #NNN" 식별자를 표시할 요소(어느 계정을 지우는지 명확화)
//
// Actions:
//   open(event)           — 삭제 버튼 클릭 시 모달 열기(대상 URL·ALT 라벨 수집)
//   confirm()             — "예" 클릭 시 DELETE 제출
//   close()               — 모달 닫기
//   closeOnOverlay(event) — 오버레이(배경) 클릭 시 닫기
// ============================================================
export default class extends Controller {
  static targets = ["modal", "altLabel"]

  // ESC 핸들러 참조(disconnect 시 제거용)와 현재 삭제 대상 URL.
  #escHandler = null
  #deleteUrl = null

  connect() {
    this.#escHandler = (e) => { if (e.key === "Escape") this.close() }
    document.addEventListener("keydown", this.#escHandler)
  }

  disconnect() {
    if (this.#escHandler) {
      document.removeEventListener("keydown", this.#escHandler)
      this.#escHandler = null
    }
    document.body.style.overflow = ""
  }

  // 모달 열기 — 클릭한 삭제 버튼의 data 속성에서 대상 URL·ALT 라벨을 읽어 저장.
  open(event) {
    event.preventDefault()
    const btn = event.currentTarget
    this.#deleteUrl = btn.dataset.deleteUrl

    if (this.hasAltLabelTarget) {
      this.altLabelTarget.textContent = btn.dataset.altLabel || ""
    }

    this.modalTarget.classList.remove("hidden")
    document.body.style.overflow = "hidden"
  }

  // "예, 삭제" — DELETE 폼을 동적 생성해 제출(Turbo 가 응답을 처리).
  confirm() {
    if (!this.#deleteUrl) return

    const csrf = document.querySelector('meta[name="csrf-token"]')?.content || ""

    const form = document.createElement("form")
    form.method = "post"
    form.action = this.#deleteUrl
    form.style.display = "none"

    // Rails 는 _method=delete 로 DELETE 라우트를 라우팅한다.
    const method = document.createElement("input")
    method.type = "hidden"
    method.name = "_method"
    method.value = "delete"
    form.appendChild(method)

    // CSRF 토큰(레일즈 위조 방지) 동봉.
    const token = document.createElement("input")
    token.type = "hidden"
    token.name = "authenticity_token"
    token.value = csrf
    form.appendChild(token)

    document.body.appendChild(form)
    this.close()
    // requestSubmit 이어야 submit 이벤트 발생 → Turbo 가로채기.
    form.requestSubmit()
  }

  close() {
    this.modalTarget.classList.add("hidden")
    document.body.style.overflow = ""
    this.#deleteUrl = null
  }

  // 오버레이(모달 컨테이너 자체)를 클릭한 경우에만 닫는다.
  closeOnOverlay(event) {
    if (event.target === this.modalTarget) this.close()
  }
}
